<template>
    <v-dialog :model-value="modelValue" max-width="960" scrollable :fullscreen="isMobile"
        @update:model-value="$emit('update:modelValue', $event)">
        <v-card v-if="dati">
            <v-toolbar density="compact" color="surface">
                <v-toolbar-title class="text-body-1">
                    Valori prestazioni — Posizione {{ posizione?.rif }}
                    <span class="text-medium-emphasis">{{ posizione?.codProgetto }}</span>
                    <span v-if="!dati.salvata" class="text-caption text-medium-emphasis ml-2">(proposta calcolata, non
                        ancora salvata)</span>
                </v-toolbar-title>
                <v-btn icon @click="chiudi"><v-icon>mdi-close</v-icon></v-btn>
            </v-toolbar>

            <v-card-text class="pt-4">
                <div class="text-caption text-medium-emphasis font-weight-medium mb-1">Prestazioni dichiarate</div>
                <v-row dense>
                    <v-col v-for="c in campiPrestazioni" :key="c.key" cols="12" sm="6" md="4">
                        <v-text-field v-model="dati[c.key]" :label="c.label" :disabled="!puoModificare"
                            density="compact" />
                    </v-col>
                </v-row>

                <div class="text-caption text-medium-emphasis font-weight-medium mt-2 mb-1">Vetri</div>
                <v-row dense>
                    <v-col cols="12" sm="6">
                        <v-text-field v-model="dati.descrizioneVetro" label="Descrizione vetro (superiore)"
                            :disabled="!puoModificare" density="compact" />
                    </v-col>
                    <v-col cols="6" sm="3">
                        <v-text-field v-model.number="dati.fattoreSolare" type="number" step="any"
                            label="Fattore solare" :disabled="!puoModificare" density="compact" />
                    </v-col>
                    <v-col cols="6" sm="3">
                        <v-text-field v-model.number="dati.trasmisLuminosa" type="number" step="any"
                            label="Trasmissione luminosa" :disabled="!puoModificare" density="compact" />
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-text-field v-model="dati.descrizioneVetroInf" label="Descrizione vetro (inferiore)"
                            :disabled="!puoModificare" density="compact" />
                    </v-col>
                    <v-col cols="6" sm="3">
                        <v-text-field v-model.number="dati.fattoreSolareInf" type="number" step="any"
                            label="Fattore solare inf." :disabled="!puoModificare" density="compact" />
                    </v-col>
                    <v-col cols="6" sm="3">
                        <v-text-field v-model.number="dati.trasmisLuminosaInf" type="number" step="any"
                            label="Trasmissione luminosa inf." :disabled="!puoModificare" density="compact" />
                    </v-col>
                </v-row>

                <div class="text-caption text-medium-emphasis font-weight-medium mt-2 mb-1">Dichiarazione</div>
                <v-row dense>
                    <v-col v-for="c in campiDichiarazione" :key="c.key" cols="12" sm="6">
                        <v-text-field v-model="dati[c.key]" :label="c.label" :disabled="!puoModificare"
                            density="compact" />
                    </v-col>
                </v-row>
            </v-card-text>

            <v-divider />
            <v-card-actions>
                <v-btn variant="text" prepend-icon="mdi-calculator-variant" :loading="calcolando"
                    :disabled="!puoModificare" @click="calcola">Calcola</v-btn>
                <v-spacer />
                <v-btn variant="text" @click="chiudi">{{ puoModificare ? 'Annulla' : 'Chiudi' }}</v-btn>
                <v-btn v-if="puoModificare" color="primary" :loading="salvando" @click="salva">Salva</v-btn>
            </v-card-actions>
        </v-card>
        <v-card v-else>
            <v-card-text class="d-flex justify-center pa-8"><v-progress-circular indeterminate /></v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
import { get_prestazioni, calcola_prestazioni, salva_prestazioni } from "@/api/api.trasmittanza.js";

const CAMPI_NUMERICI = ["fattoreSolare", "trasmisLuminosa", "fattoreSolareInf", "trasmisLuminosaInf"];

export default {
    name: "PrestazioniPosizione",

    props: {
        modelValue: { type: Boolean, default: false },
        commessaId: { type: [String, Number], required: true },
        posizione: { type: Object, default: null },
        puoModificare: { type: Boolean, default: false },
        // Riempimenti correnti { nrRiempimenti, vetroSup, vetroInt, vetroInf } della videata di trasmittanza:
        // se assente il calcolo usa gli articoli salvati sulla posizione.
        riempimenti: { type: Object, default: null },
    },
    emits: ["update:modelValue", "salvato"],

    data() {
        return {
            dati: null,
            calcolando: false,
            salvando: false,
            campiPrestazioni: [
                { key: "acustica", label: "Acustica" },
                { key: "aria", label: "Permeabilità all'aria" },
                { key: "acqua", label: "Tenuta all'acqua" },
                { key: "ventoPressione", label: "Resistenza al vento" },
                { key: "urto", label: "Resistenza all'urto" },
                { key: "capacitaPortante", label: "Capacità portante" },
                { key: "capacitaSblocco", label: "Capacità di sblocco" },
            ],
            campiDichiarazione: [
                { key: "produttore", label: "Produttore" },
                { key: "impiego", label: "Impiego" },
                { key: "norma", label: "Norma" },
                { key: "modello", label: "Modello" },
                { key: "profilo", label: "Profilo" },
                { key: "spessoreTelaio", label: "Spessore telaio" },
                { key: "spessoreAnta", label: "Spessore anta" },
                { key: "laboratorio", label: "Laboratorio" },
                { key: "sistemaAttestazione", label: "Sistema di attestazione" },
                { key: "annoProduzione", label: "Anno di produzione" },
                { key: "sostanzeDannose", label: "Sostanze dannose" },
            ],
        };
    },

    computed: {
        isMobile() {
            return this.$vuetify.display.smAndDown;
        },
    },

    watch: {
        modelValue(aperto) {
            if (aperto) this.carica();
        },
    },

    methods: {
        async carica() {
            this.dati = null;
            if (!this.posizione) return;
            try {
                const resp = await get_prestazioni(this.commessaId, this.posizione.id);
                this.dati = resp.data;
            } catch (error) {
                console.log(error);
                this.$emit("update:modelValue", false);
            }
        },

        // v-model.number lascia "" nei campi svuotati: il backend si aspetta numeri.
        payload() {
            const d = { ...this.dati };
            for (const k of CAMPI_NUMERICI) d[k] = Number(d[k]) || 0;
            return d;
        },

        async calcola() {
            this.calcolando = true;
            try {
                const resp = await calcola_prestazioni(this.commessaId, this.posizione.id, this.riempimenti || undefined);
                this.dati = resp.data;
            } catch (error) {
                console.log(error);
            }
            this.calcolando = false;
        },

        async salva() {
            this.salvando = true;
            try {
                const resp = await salva_prestazioni(this.commessaId, this.posizione.id, this.payload());
                this.dati = resp.data;
                this.$emit("salvato");
                this.$emit("update:modelValue", false);
            } catch (error) {
                console.log(error);
            }
            this.salvando = false;
        },

        chiudi() {
            this.$emit("update:modelValue", false);
        },
    },
};
</script>
